import { useMediaQuery } from '@mantine/hooks'
import ActionButton from 'components/CourseInfo/ActionButton'
import KlassInfoHeader from 'components/CourseInfo/KlassInfoHeader'
import KlassInfoSection from 'components/CourseInfo/KlassInfoSection'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { useAuth } from 'shared/contexts/Auth'
import { useCourseStatus } from 'shared/hooks/useCourseStatus'
import { useProgressNow } from 'shared/hooks/useProgressNow'
import { IChapter, ICourse } from 'stores/courses'

interface IEventInfo {
  course: ICourse
  curriculum: IChapter[]
}

const EventInfo = ({ course, curriculum }: IEventInfo) => {
  const { courseStatus } = useCourseStatus(course.id)
  const { isSigned } = useAuth()
  const { id: resourceId } = useProgressNow(course.id, isSigned)
  const isMobile = useMediaQuery('max-width:1023px')
  const isEndedEvent = dayjs().isAfter(course.endedAt)
  const router = useRouter()

  const applyStatusHandler = () => {
    // 이벤트 기간 = 교육기간이 오늘 이후면 -> 지난 이벤트
    // 교육 기간이 오늘 이전이면 -> 다가오는 이벤트
    return isEndedEvent ? 'closed' : 'apply'
  }
  const buttonLabelHandler = () => {
    if (courseStatus === 'loading') return 'Loading'
    if (!isEndedEvent && courseStatus === 'enrollment') {
      return '결제하기'
    }
    if (!isEndedEvent && courseStatus === 'play') {
      return '학습하기'
    }
    return '신청 종료'
  }

  const goToOrder = useCallback(
    (courseId: string) => {
      return router.push(`/courses/technology/${courseId}/order`).then(() => window.scrollTo(0, 0))
    },
    [router],
  )

  const goToPlayer = useCallback(
    (courseId: string) => {
      if (isSigned && resourceId && !isEmpty(curriculum) && courseStatus === 'play') {
        const chapterId = curriculum.find(chapter =>
          chapter.resources.find(resource => resource.id === resourceId),
        )?.id as string
        return router.push(`/courses/technology/${courseId}/${chapterId}/${resourceId}`)
      }
    },
    [courseStatus, curriculum, isSigned, resourceId, router],
  )

  const priceToString = (price: number) => {
    return price?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  return (
    <div className="px-4 lg:rounded-lg lg:border lg:border-gray-300 lg:p-6 lg:pt-5">
      <KlassInfoHeader
        isMobile={isMobile}
        klassId={course.id}
        title={course.title}
        panelText={course.panelText}
        shareUrl={course.shareUrl}
        imgUrl={course.imageM}
      />
      <hr className="mb-4 w-full border-gray-300 lg:hidden" />
      <div className="flex flex-col">
        <KlassInfoSection
          course={course}
          isKDT={false}
          isSimplifyKDT={false}
          applyStatus={applyStatusHandler()}
        />
        <hr className="my-4 border-gray-300" />
        <div className="flex flex-col space-y-1">
          <div className="flex text-sm text-gray-500"></div>
          <div className="flex items-baseline space-x-1">
            <div className="flex items-baseline space-x-1">
              <span className="text-xl font-semibold leading-tight">
                {priceToString(course.price)}원
              </span>
            </div>
          </div>
        </div>
        <div className="fixed bottom-0 left-0 right-0 z-20 border border-t bg-white px-4 pb-4 lg:static lg:border-none lg:px-0 lg:pb-0">
          <ActionButton
            course={course}
            klassId={course.id}
            disabled={courseStatus === 'loading' || isEndedEvent}
            handler={() => (courseStatus === 'play' ? goToPlayer(course.id) : goToOrder(course.id))}
            name={buttonLabelHandler()}
          />
        </div>
      </div>
    </div>
  )
}

export default EventInfo
